import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  Typography,
  Image,
  Tag,
  Card,
  Descriptions,
  Spin,
} from "antd";
import moment from "moment";
import _get from "lodash/get";

import BackButton from "components/BackButton";
import { getCandidateDetailRequest } from "providers/CandidateProvider/slice";
import "./styles.less";

const CandidateDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const candidateDetail = useSelector(
    (state) => state.candidateSlice.candidateDetail
  );
  const isLoading = useSelector((state) => state.candidateSlice.isLoading);

  useEffect(() => {
    if (id) {
      dispatch(getCandidateDetailRequest(id));
    }
  }, [id, dispatch]);

  if (isLoading || !candidateDetail) {
    return (
      <div className="candidate-detail-wrapper loading-container">
        <Spin size="large" />
      </div>
    );
  }

  const {
    fullName,
    contestNumber,
    hometown,
    currentResidence,
    message,
    introduction,
    informationDetail,
    videoUrl,
    images,
    contestId,
    voteCount,
    totalEngagement,
    status,
    createdAt,
  } = candidateDetail;

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]{11}).*/;
    const match = url.match(regExp);

    if (match && match[2]) {
      return match[2];
    }
    return null;
  };

  const videoId = getYouTubeId(videoUrl);

  const statusConfig = {
    active: { color: "green", text: "Hoạt động" },
    inactive: { color: "red", text: "Không hoạt động" },
    draft: { color: "orange", text: "Bản nháp" },
  };
  const currentStatus = statusConfig[status] || {
    color: "default",
    text: status,
  };

  return (
    <div className="candidate-detail-wrapper">
      <Row className="candidate-detail-header" justify="start" align="middle">
        <BackButton />
        <Typography.Title level={4}>Chi tiết thí sinh</Typography.Title>
      </Row>

      <div className="detail-content">
        <Row gutter={[24, 24]}>
          <Col xs={24} md={8}>
            <Card title="Ảnh đại diện & Bình chọn">
              <div className="main-image-container">
                <Image
                  width="100%"
                  src={_get(images, "[0].url")}
                  alt={fullName}
                />
              </div>
              <Descriptions column={1} bordered style={{ marginTop: "20px" }}>
                <Descriptions.Item label="Lượt bình chọn">
                  <Tag
                    color="blue"
                    style={{ fontSize: "16px", padding: "5px 10px" }}
                  >
                    {voteCount || 0}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Lượt tương tác">
                  <Tag
                    color="green"
                    style={{ fontSize: "16px", padding: "5px 10px" }}
                  >
                    {totalEngagement || 0}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} md={16}>
            <Card title="Thông tin cơ bản">
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Họ và tên">
                  {fullName}
                </Descriptions.Item>
                <Descriptions.Item label="Số báo danh">
                  {contestNumber}
                </Descriptions.Item>
                <Descriptions.Item label="Cuộc thi">
                  {_get(contestId, "name", "N/A")}
                </Descriptions.Item>
                <Descriptions.Item label="Trạng thái">
                  <Tag color={currentStatus.color}>{currentStatus.text}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Quê quán">
                  {hometown}
                </Descriptions.Item>
                <Descriptions.Item label="Nơi ở hiện tại">
                  {currentResidence}
                </Descriptions.Item>
                <Descriptions.Item label="Ngày tham gia" span={2}>
                  {moment(createdAt).format("DD/MM/YYYY HH:mm")}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>

        <Card title="Thông điệp" style={{ marginTop: "24px" }}>
          <div
            className="html-content"
            dangerouslySetInnerHTML={{ __html: message }}
          />
        </Card>

        <Card title="Giới thiệu" style={{ marginTop: "24px" }}>
          <div
            className="html-content"
            dangerouslySetInnerHTML={{ __html: introduction }}
          />
        </Card>

        <Card title="Thông tin chi tiết" style={{ marginTop: "24px" }}>
          <div
            className="html-content"
            dangerouslySetInnerHTML={{ __html: informationDetail }}
          />
        </Card>

        <Card title="Thư viện ảnh" style={{ marginTop: "24px" }}>
          <Image.PreviewGroup>
            <Row gutter={[16, 16]}>
              {(images || []).map((img, index) => (
                <Col key={index} xs={12} sm={8} md={6}>
                  <Image
                    src={img.url}
                    alt={`${fullName}-image-${index}`}
                    style={{
                      width: "100%",
                      height: "150px",
                      objectFit: "cover",
                    }}
                  />
                </Col>
              ))}
            </Row>
          </Image.PreviewGroup>
        </Card>

        <Card title="Video" style={{ marginTop: "24px" }}>
          {(() => {
            if (!videoUrl) {
              return <p>Không có video.</p>;
            }
            if (videoId) {
              return (
                <div className="video-responsive">
                  <iframe
                    width="100%"
                    height="450"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    title="YouTube video player"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              );
            }
            return (
              <video width="100%" controls src={videoUrl}>
                Your browser does not support the video tag.
              </video>
            );
          })()}
        </Card>
      </div>
    </div>
  );
};

export default CandidateDetail;
